const ACCESS_TOKEN_EXPIRES_AT_KEY = 'dashboard_access_token_expires_at';
const REFRESH_LOCK_KEY = 'dashboard_refresh_lock';
const AUTH_CHANNEL_NAME = 'dashboard_auth_channel';

const REFRESH_BUFFER_MS = 3 * 60 * 1000; // refresh 3 min before expiry
const LOCK_MAX_AGE_MS = 20 * 1000;   // stale lock protection

let inMemoryRefreshPromise = null;
let authChannel = null;

function parseExpiryToMs(raw) {
    if (raw == null || raw === '') return NaN;
  
    const trimmed = String(raw).trim();
  
    // Numeric (integer or decimal) epoch
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return NaN;
  
      // If value is too small to be milliseconds, treat as seconds
      // 1e12 ms ~= 2001-09-09, so anything below that is probably seconds
      if (n < 1e12) {
        return Math.floor(n * 1000);
      }
  
      return Math.floor(n);
    }
  
    // ISO/date string fallback
    const ms = new Date(trimmed).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  }

function getChannel() {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!authChannel) {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  }
  return authChannel;
}

export function setAccessTokenExpiresAt(expiresAt) {
  if (typeof window === 'undefined' || !expiresAt) return;
  localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, expiresAt);
}

export function getAccessTokenExpiresAt() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
}

export function clearAccessTokenExpiresAt() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
}

export function isAccessTokenExpiringSoon() {
    const raw = getAccessTokenExpiresAt();
    if (!raw) return true;
  
    const expiryMs = parseExpiryToMs(raw);
    if (!Number.isFinite(expiryMs)) return true;
  
    return Date.now() >= (expiryMs - REFRESH_BUFFER_MS);
  }

function getRefreshLock() {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isLockActive(lockData) {
  if (!lockData?.startedAt) return false;
  return (Date.now() - lockData.startedAt) < LOCK_MAX_AGE_MS;
}

function createTabId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function tryAcquireRefreshLock(tabId) {
  const existing = getRefreshLock();
  if (isLockActive(existing)) {
    return false;
  }

  const newLock = {
    tabId,
    startedAt: Date.now(),
  };

  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(newLock));

  const confirm = getRefreshLock();
  return confirm?.tabId === tabId;
}

function releaseRefreshLock(tabId) {
  const existing = getRefreshLock();
  if (existing?.tabId === tabId) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;

  function escapeRe(s) {
    return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1');
  }

  const match = document.cookie.match(
    RegExp('(?:^|;\\s*)' + escapeRe(name) + '=([^;]*)')
  );
  return match ? match[1] : null;
}

export function getDashboardUserCreds() {
  const raw = getCookie('dashboard_login_user_creds');
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function waitForAnotherTabRefresh(timeoutMs = 25000) {
  return new Promise((resolve, reject) => {
    const channel = getChannel();
    const startedAt = Date.now();

    const onMessage = (event) => {
      if (event?.data?.type === 'refresh_success') {
        cleanup();
        resolve(true);
      }
      if (event?.data?.type === 'refresh_failed') {
        cleanup();
        reject(new Error('Refresh failed in another tab'));
      }
    };

    const onStorage = (event) => {
      if (
        event.key === ACCESS_TOKEN_EXPIRES_AT_KEY &&
        event.newValue
      ) {
        cleanup();
        resolve(true);
      }

      if (
        event.key === REFRESH_LOCK_KEY &&
        event.newValue === null
      ) {
        // lock released; check if expiry was updated
        const expiry = getAccessTokenExpiresAt();
        if (expiry) {
          cleanup();
          resolve(true);
        }
      }
    };

    const timer = setInterval(() => {
      if (Date.now() - startedAt > timeoutMs) {
        cleanup();
        reject(new Error('Timed out waiting for refresh'));
      }
    }, 300);

    function cleanup() {
      clearInterval(timer);
      window.removeEventListener('storage', onStorage);
      if (channel) {
        channel.removeEventListener('message', onMessage);
      }
    }

    window.addEventListener('storage', onStorage);
    if (channel) {
      channel.addEventListener('message', onMessage);
    }
  });
}

async function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  
  async function callGenerateRefreshTokenWithRetry(server, payload) {
    const maxAttempts = 3;
    let lastResp = null;
    let lastError = null;
  
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await server.generateRefreshToken(payload);
        lastResp = resp;
  
        // Success
        if (resp.status === 200) {
          return resp;
        }
  
        // Retry only for transient server errors
        if (resp.status >= 500 && resp.status < 600 && attempt < maxAttempts) {
          await sleep(500 * attempt); // 500ms, 1000ms
          continue;
        }else{
          await server.logout();
          document.cookie = "dashboard_login_user_creds=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=None";
          window.dispatchEvent(new Event('storage'));
          window.location.href = '/login';
        }
  
        return resp;
      } catch (error) {
        lastError = error;
  
        if (attempt < maxAttempts) {
          await sleep(500 * attempt);
          continue;
        }
  
        throw error;
      }
    }
  
    if (lastResp) return lastResp;
    throw lastError || new Error('Refresh token request failed');
  }

export async function refreshAccessTokenIfNeeded(server, { force = false } = {}) {
  if (!force && !isAccessTokenExpiringSoon()) {
    return true;
  }
   // NEW: if another tab already refreshed very recently, skip
   if (wasRefreshDoneRecently()) {
    return true;
  }

  if (inMemoryRefreshPromise) {
    return inMemoryRefreshPromise;
  }

  const user = getDashboardUserCreds();
  if (!user?.client_id || !user?.email) {
    throw new Error('Missing dashboard user credentials');
  }

  const tabId = createTabId();

  if (!tryAcquireRefreshLock(tabId)) {
    return waitForAnotherTabRefresh();
  }
  // IMPORTANT: check again after acquiring lock,
  // because another tab may have refreshed just before lock release
  if (wasRefreshDoneRecently()) {
    releaseRefreshLock(tabId);
    return true;
  }

  const channel = getChannel();

  inMemoryRefreshPromise = (async () => {
    try {
      channel?.postMessage({ type: 'refresh_started' });

      const resp = await callGenerateRefreshTokenWithRetry(server, {
        client_id: user.client_id,
        email: user.email,
      });

      if (resp.status !== 200) {
        throw new Error(`Refresh failed with status ${resp.status}`);
      }

      const data = await resp.json();

      if (data?.access_token_expires_at) {
        setAccessTokenExpiresAt(data.access_token_expires_at);
      }
      // NEW
      markRefreshSuccessNow();

      channel?.postMessage({
        type: 'refresh_success',
        access_token_expires_at: data?.access_token_expires_at || null,
      });

      return true;
    } catch (error) {
      channel?.postMessage({ type: 'refresh_failed' });
      throw error;
    } finally {
      releaseRefreshLock(tabId);
      inMemoryRefreshPromise = null;
    }
  })();

  return inMemoryRefreshPromise;
}

let refreshTimer = null;

let schedulerInitialized = false;

const LAST_REFRESH_SUCCESS_AT_KEY = 'dashboard_last_refresh_success_at';
const CROSS_TAB_REFRESH_COOLDOWN_MS = 90 * 1000;

function getLastRefreshSuccessAt() {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(LAST_REFRESH_SUCCESS_AT_KEY);
  if (!raw) return null;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function markRefreshSuccessNow() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LAST_REFRESH_SUCCESS_AT_KEY, String(Date.now()));
}

function wasRefreshDoneRecently() {
  const last = getLastRefreshSuccessAt();
  if (last == null) return false;
  return (Date.now() - last) < CROSS_TAB_REFRESH_COOLDOWN_MS;
}

export function scheduleAccessTokenRefresh(server) {
  if (schedulerInitialized) {
    return; // 🔴 prevent re-scheduling
  }
  schedulerInitialized = true;

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const raw = getAccessTokenExpiresAt();
  if (!raw) return;

  const expiryMs = parseExpiryToMs(raw);
  if (!Number.isFinite(expiryMs)) {
    console.error('Invalid access token expiry value:', raw);
    return;
  }

  const refreshAtMs = expiryMs - REFRESH_BUFFER_MS;
  const delay = Math.max(0, refreshAtMs - Date.now());

  console.log('Scheduling token refresh', { raw, expiryMs, refreshAtMs, now: Date.now(), delay });

  refreshTimer = setTimeout(async () => {
    try {
      await refreshAccessTokenIfNeeded(server, { force: true });
    } catch (error) {
      console.error('Scheduled token refresh failed:', error);
    } finally {
      schedulerInitialized = false; // allow next cycle
      scheduleAccessTokenRefresh(server);
    }
  }, delay);
}

export function stopAccessTokenRefreshScheduler() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}